import { jsx as _jsx } from "react/jsx-runtime";
function sp(key) {
    return `var(--space-${key})`;
}
/**
 * Decompose CSS shorthand properties into longhand. React 19's hydration
 * validator reads `element.style.<longhand>` back from the DOM and compares it
 * to the client virtual-DOM `style` object. The browser stores `flex` and
 * `gap` as shorthand internally — when set via shorthand with a CSS variable
 * value, reads of `style.rowGap` / `style.flexGrow` return empty strings on
 * some property paths, producing false-positive hydration mismatches. Emitting
 * longhand (`rowGap`/`columnGap`, `flexGrow`/`flexShrink`/`flexBasis`) avoids
 * the issue entirely and is what the primitives canonically serialize.
 *
 * Only the shorthand forms we actually use in inline `style` props are
 * normalized — there is no need to handle every shorthand in CSS.
 */
export function normalizeStyle(style) {
    if (!style)
        return style;
    // Avoid scanning when neither problematic shorthand is present.
    if (style.flex == null && style.gap == null)
        return style;
    const out = {};
    for (const [key, value] of Object.entries(style)) {
        if (key === 'flex' && (typeof value === 'string' || typeof value === 'number')) {
            const decomposed = decomposeFlex(value);
            if (decomposed) {
                Object.assign(out, decomposed);
                continue;
            }
            // Unrecognized shape — fall through and keep the original.
        }
        if (key === 'gap' && (typeof value === 'string' || typeof value === 'number')) {
            out.rowGap = value;
            out.columnGap = value;
            continue;
        }
        out[key] = value;
    }
    return out;
}
function decomposeFlex(value) {
    // `flex: <number>` shorthand: `flex: 1` → `1 1 0%`.
    if (typeof value === 'number') {
        return { flexGrow: value, flexShrink: 1, flexBasis: '0%' };
    }
    // CSS keywords don't decompose cleanly; leave them alone.
    if (['none', 'auto', 'initial', 'inherit', 'unset', 'revert'].includes(value.trim())) {
        return null;
    }
    const parts = value.trim().split(/\s+/);
    // 1-value: `flex: 1` (already handled above as number, but accept string form too)
    //          `flex: 100px` → basis only
    if (parts.length === 1) {
        const v = parts[0];
        if (/^-?\d*\.?\d+$/.test(v)) {
            return { flexGrow: Number(v), flexShrink: 1, flexBasis: '0%' };
        }
        return { flexGrow: 1, flexShrink: 1, flexBasis: v };
    }
    // 2-value: `flex: <grow> <shrink>` or `flex: <grow> <basis>`
    if (parts.length === 2) {
        const [a, b] = parts;
        if (/^-?\d*\.?\d+$/.test(b)) {
            return { flexGrow: Number(a), flexShrink: Number(b), flexBasis: '0%' };
        }
        return { flexGrow: Number(a), flexShrink: 1, flexBasis: b };
    }
    // 3-value: `flex: <grow> <shrink> <basis>` (the canonical form we use)
    if (parts.length === 3) {
        const [g, s, basis] = parts;
        return { flexGrow: Number(g), flexShrink: Number(s), flexBasis: basis };
    }
    return null;
}
export function boxStyle(props) {
    const { p, px, py, pt, pr, pb, pl, m, mx, my, mt, mr, mb, ml, ps, pe, ms, me, pbs, pbe, mbs, mbe, rounded, shadow, border, bg, color, } = props;
    const style = {};
    // Physical padding
    if (p)
        style.padding = sp(p);
    if (px) {
        style.paddingLeft = sp(px);
        style.paddingRight = sp(px);
    }
    if (py) {
        style.paddingTop = sp(py);
        style.paddingBottom = sp(py);
    }
    if (pt)
        style.paddingTop = sp(pt);
    if (pr)
        style.paddingRight = sp(pr);
    if (pb)
        style.paddingBottom = sp(pb);
    if (pl)
        style.paddingLeft = sp(pl);
    // Physical margin
    if (m)
        style.margin = sp(m);
    if (mx) {
        style.marginLeft = sp(mx);
        style.marginRight = sp(mx);
    }
    if (my) {
        style.marginTop = sp(my);
        style.marginBottom = sp(my);
    }
    if (mt)
        style.marginTop = sp(mt);
    if (mr)
        style.marginRight = sp(mr);
    if (mb)
        style.marginBottom = sp(mb);
    if (ml)
        style.marginLeft = sp(ml);
    // Logical padding
    if (ps)
        style.paddingInlineStart = sp(ps);
    if (pe)
        style.paddingInlineEnd = sp(pe);
    if (pbs)
        style.paddingBlockStart = sp(pbs);
    if (pbe)
        style.paddingBlockEnd = sp(pbe);
    // Logical margin
    if (ms)
        style.marginInlineStart = sp(ms);
    if (me)
        style.marginInlineEnd = sp(me);
    if (mbs)
        style.marginBlockStart = sp(mbs);
    if (mbe)
        style.marginBlockEnd = sp(mbe);
    // Visual
    if (rounded)
        style.borderRadius = `var(--radius-${rounded})`;
    if (shadow)
        style.boxShadow = `var(--shadow-${shadow})`;
    if (border && border !== 'none') {
        style.border = `var(--border-${border}) solid var(--border)`;
    }
    else if (border === 'none') {
        style.border = 'none';
    }
    if (bg)
        style.background = `var(--${bg})`;
    if (color)
        style.color = `var(--${color})`;
    return style;
}
const BOX_LAYOUT_PROP_KEYS = new Set([
    'p', 'px', 'py', 'pt', 'pr', 'pb', 'pl',
    'm', 'mx', 'my', 'mt', 'mr', 'mb', 'ml',
    'ps', 'pe', 'ms', 'me', 'pbs', 'pbe', 'mbs', 'mbe',
    'rounded', 'shadow', 'border', 'bg', 'color',
]);
function splitBoxLayoutProps(props) {
    const layout = {};
    const dom = {};
    for (const k of Object.keys(props)) {
        (BOX_LAYOUT_PROP_KEYS.has(k) ? layout : dom)[k] = props[k];
    }
    return { layout: layout, dom };
}
export function Box({ as: Tag = 'div', children, className, style: extraStyle, ...rest }) {
    const { layout, dom } = splitBoxLayoutProps(rest);
    const style = {
        ...boxStyle(layout),
        ...normalizeStyle(extraStyle),
    };
    return (_jsx(Tag, { className: className, style: style, ...dom, children: children }));
}
//# sourceMappingURL=Box.js.map